import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Calendar,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface DatePickerProps {
  value: string; // 'YYYY-MM-DD' or ''
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  align?: 'left' | 'right'; // 日历面板对齐方向，默认 'left'
}

const MONTH_NAMES_EN = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const MONTH_NAMES_ZH = [
  '一月',
  '二月',
  '三月',
  '四月',
  '五月',
  '六月',
  '七月',
  '八月',
  '九月',
  '十月',
  '十一月',
  '十二月',
];
const WEEKDAY_NAMES_EN = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const WEEKDAY_NAMES_ZH = ['一', '二', '三', '四', '五', '六', '日'];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1; // Monday = 0
}

function fmtDate(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function formatDisplay(value: string, isZh: boolean): string {
  if (!value) return '';
  const [y, m, d] = value.split('-').map(Number);
  if (isZh) {
    return `${y}年${m}月${d}日`;
  }
  const monthName = MONTH_NAMES_EN[m - 1]?.slice(0, 3) || '';
  return `${monthName} ${d}, ${y}`;
}

export default function DatePicker({
  value,
  onChange,
  placeholder,
  className,
  align = 'left',
}: DatePickerProps) {
  const { t, i18n } = useTranslation();
  const isZh =
    i18n.language === 'zh' ||
    i18n.language === 'zh-cn' ||
    i18n.language === 'zh-CN';

  const today = useMemo(() => {
    const d = new Date();
    return {
      year: d.getFullYear(),
      month: d.getMonth(),
      date: d.getDate(),
      str: fmtDate(d.getFullYear(), d.getMonth(), d.getDate()),
    };
  }, []);

  const initialYear = value ? parseInt(value.split('-')[0]) : today.year;
  const initialMonth = value ? parseInt(value.split('-')[1]) - 1 : today.month;

  const [isOpen, setIsOpen] = useState(false);
  const [viewYear, setViewYear] = useState(initialYear);
  const [viewMonth, setViewMonth] = useState(initialMonth);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync view when value changes externally (e.g. quick select)
  useEffect(() => {
    if (value) {
      const [y, m] = value.split('-').map(Number);
      setViewYear(y);
      setViewMonth(m - 1);
    }
  }, [value]);

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    // Use setTimeout to avoid immediate close when opening
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClick);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [isOpen]);

  const prevYear = useCallback(() => {
    setViewYear(y => y - 1);
  }, []);

  const nextYear = useCallback(() => {
    setViewYear(y => y + 1);
  }, []);

  const prevMonth = useCallback(() => {
    setViewMonth(m => {
      if (m === 0) {
        setViewYear(y => y - 1);
        return 11;
      }
      return m - 1;
    });
  }, []);

  const nextMonth = useCallback(() => {
    setViewMonth(m => {
      if (m === 11) {
        setViewYear(y => y + 1);
        return 0;
      }
      return m + 1;
    });
  }, []);

  const handleSelectDay = useCallback(
    (day: number) => {
      onChange(fmtDate(viewYear, viewMonth, day));
      setIsOpen(false);
    },
    [viewYear, viewMonth, onChange]
  );

  const handleClear = useCallback(() => {
    onChange('');
    setIsOpen(false);
  }, [onChange]);

  const handleToday = useCallback(() => {
    onChange(today.str);
    setIsOpen(false);
  }, [onChange, today.str]);

  const monthNames = isZh ? MONTH_NAMES_ZH : MONTH_NAMES_EN;
  const weekdayNames = isZh ? WEEKDAY_NAMES_ZH : WEEKDAY_NAMES_EN;

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  // Build calendar grid
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  // Pad to complete the last row (always 7 columns)
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div ref={containerRef} className={`relative ${className || ''}`}>
      {/* Input Display */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent cursor-pointer hover:border-gray-400 transition-colors bg-white text-left min-h-[38px]"
      >
        <span className={value ? 'text-gray-900' : 'text-gray-400'}>
          {value ? formatDisplay(value, isZh) : placeholder || ''}
        </span>
        <Calendar size={15} className="text-gray-400 flex-shrink-0 ml-2" />
      </button>

      {/* Calendar Dropdown */}
      {isOpen && (
        <div
          className={`absolute top-full mt-1 w-[280px] bg-white border border-gray-200 rounded-lg shadow-xl z-[60] p-3 select-none ${align === 'right' ? 'right-0' : 'left-0'}`}
        >
          {/* Header: Year/Month Navigation */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={prevYear}
                className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                title={isZh ? '上一年' : 'Previous year'}
              >
                <ChevronsLeft size={16} className="text-gray-600" />
              </button>
              <button
                type="button"
                onClick={prevMonth}
                className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                title={isZh ? '上一月' : 'Previous month'}
              >
                <ChevronLeft size={16} className="text-gray-600" />
              </button>
            </div>
            <span className="text-sm font-semibold text-gray-800">
              {isZh
                ? `${viewYear}年 ${monthNames[viewMonth]}`
                : `${monthNames[viewMonth]} ${viewYear}`}
            </span>
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={nextMonth}
                className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                title={isZh ? '下一月' : 'Next month'}
              >
                <ChevronRight size={16} className="text-gray-600" />
              </button>
              <button
                type="button"
                onClick={nextYear}
                className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                title={isZh ? '下一年' : 'Next year'}
              >
                <ChevronsRight size={16} className="text-gray-600" />
              </button>
            </div>
          </div>

          {/* Weekday Headers */}
          <div className="grid grid-cols-7 mb-1">
            {weekdayNames.map((d, i) => (
              <div
                key={i}
                className="text-center text-xs text-gray-400 py-1.5 font-medium"
              >
                {d}
              </div>
            ))}
          </div>

          {/* Day Grid */}
          <div className="grid grid-cols-7">
            {cells.map((day, i) => {
              if (day === null) {
                return (
                  <div key={`empty-${i}`} className="w-full aspect-square" />
                );
              }
              const dateStr = fmtDate(viewYear, viewMonth, day);
              const isSelected = dateStr === value;
              const isToday = dateStr === today.str;

              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => handleSelectDay(day)}
                  className={`w-full aspect-square flex items-center justify-center text-sm rounded-md transition-colors
                    ${
                      isSelected
                        ? 'bg-gray-900 text-white font-semibold'
                        : isToday
                          ? 'bg-blue-50 text-blue-700 font-semibold ring-1 ring-blue-200'
                          : 'text-gray-700 hover:bg-gray-100'
                    }`}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Footer: Clear & Today */}
          <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={handleClear}
              disabled={!value}
              className={`text-xs transition-colors px-2 py-1 ${
                !value
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('chatsPage.clearFilter')}
            </button>
            <button
              type="button"
              onClick={handleToday}
              className="text-xs bg-gray-900 text-white font-medium hover:bg-gray-800 transition-colors px-3 py-1 rounded-md"
            >
              {t('chatsPage.today')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
